<template>
  <Alert v-if="errorMsgs.length" type="danger" :messages="errorMsgs"></Alert>

  <div class="b-leaseitnow-car-config">
    <Loader v-if="isLoading" display="absolute"></Loader>

    <div class="b-leaseitnow-car-config__totals">
      <div class="b-leaseitnow-car-config__slider-pair">
        <em class="h4">{{ dictionary.retailPriceBeforeTaxesLabel }}</em>
        <span>€{{ priceExclTaxFormatted }}</span>
      </div>
      <div class="b-leaseitnow-car-config__slider-pair">
        <em class="h4">{{ dictionary.retailPriceLabel }}</em>
        <span>€{{ priceFormatted }}</span>
      </div>
      <div
        v-show="extraPrice !== 0"
        class="b-leaseitnow-car-config__slider-pair">
        <em
          class="h4"
          data-bs-toggle="tooltip"
          :title="dictionary.extrasPriceTooltip">
          {{ dictionary.extrasPriceLabel }}
          <LazyImage
            :image="{
              url: '/imgs/icons/tooltip.svg',
              alt: 'Tooltip',
            }"
            width="16" />
        </em>
        <span>€{{ extraPriceFormatted }}</span>
      </div>
      <div
        v-show="extraPrice !== 0"
        class="b-leaseitnow-car-config__slider-pair">
        <em class="h4">{{ dictionary.finalPriceLabel }}</em>
        <span>€{{ finalPriceFormatted }}</span>
      </div>
    </div>

    <div class="b-leaseitnow-car-config__header mb-24">
      <div class="h2 extrabold">{{ dictionary.monthlyRentalLabel }}</div>
      <div>
        <div
          class="b-leaseitnow-car-config__price-w-vat"
          :class="{ 'is-active': vatIncluded }">
          <div class="bold mb-5">
            <span class="animate">
              <em v-if="vatIncluded">€{{ rentalPriceVatIncludedFormatted }}</em>
              <em v-else>€{{ rentalPriceFormatted }}</em>
              <span class="suffix">
                {{ dictionary.vehiclePriceSuffix }}
              </span>
            </span>
          </div>

          <div class="form-check form-switch">
            <label class="form-check-label" for="vat">
              <span v-if="vatIncluded">
                {{ dictionary.vehicleVatIncluded }}
              </span>
              <span v-else>
                {{ dictionary.vehicleVatExcluded }}
              </span>
            </label>
            <input
              id="vat"
              v-model="vatIncluded"
              class="form-check-input"
              type="checkbox"
              role="switch"
              @change="onChangeVat" />
          </div>
        </div>
      </div>
    </div>

    <hr class="separator theme-yellow mb-12" />

    <!-- item -->
    <div class="mb-32">
      <div class="b-leaseitnow-car-config__slider-pair mb-10">
        <em
          class="h4"
          data-bs-toggle="tooltip"
          :title="dictionary.depositTooltipText">
          {{ dictionary.depositLabel }}
          <LazyImage
            :image="{
              url: '/imgs/icons/tooltip.svg',
              alt: 'Tooltip',
            }"
            width="16" />
        </em>
        <span v-if="!vatIncluded">€{{ deposit?.toLocaleString(lang) }}</span>
        <span v-else>€{{ desopitWithVat?.toLocaleString(lang) }}</span>
      </div>
      <div>
        <VueSlider
          v-model="deposit"
          v-bind="sliderOptions"
          :min="depositConfiguration?.min"
          :max="depositConfiguration?.max"
          :interval="configuration.deposit.interval"
          :tooltip-formatter="(val) => depositTooltipFormatted(val)"
          @change="onChange"></VueSlider>
      </div>
    </div>
    <!-- item -->
    <div class="mb-32">
      <div class="b-leaseitnow-car-config__slider-pair mb-10">
        <em class="h4">{{ dictionary.numOfPaymentsLabel }}</em>
        <span>
          {{ numOfPayments }} {{ dictionary.numOfPaymentsValueSuffix }}
        </span>
      </div>
      <div>
        <VueSlider
          v-model="numOfPayments"
          v-bind="sliderOptions"
          :min="configuration.numOfPayments.min"
          :max="configuration.numOfPayments.max"
          :interval="configuration.numOfPayments.interval"
          @change="onChange" />
      </div>
    </div>
    <!-- item -->
    <div class="mb-32">
      <div class="b-leaseitnow-car-config__slider-pair mb-10">
        <em class="h4">{{ dictionary.annualKmLabel }}</em>
        <span>{{ annualKm?.toLocaleString(lang) }}</span>
      </div>
      <div>
        <VueSlider
          v-model="annualKm"
          v-bind="sliderOptions"
          :min="configuration.annualKm.min"
          :max="configuration.annualKm.max"
          :interval="configuration.annualKm.interval"
          :tooltip-formatter="(val) => `${val.toLocaleString(lang)}`"
          @change="onChange" />
      </div>
    </div>

    <div class="mb-30">
      <div class="form-check form-switch">
        <label class="form-check-label" for="buyback">
          <span>{{ dictionary.buybackLabel }}</span>
        </label>
        <input
          id="buyback"
          v-model="buyback"
          class="form-check-input"
          type="checkbox"
          role="switch"
          @change="onChangeBuyback" />
      </div>
    </div>

    <!-- item -->
    <div v-if="buyback" class="mb-32">
      <div class="b-leaseitnow-car-config__slider-pair mb-10">
        <em
          class="h4"
          data-bs-toggle="tooltip"
          :title="dictionary.buybackAmountTooltipText">
          {{ dictionary.buybackAmountLabel }}
          <LazyImage
            :image="{
              url: '/imgs/icons/tooltip.svg',
              alt: 'Tooltip',
            }"
            width="16" />
        </em>
        <span v-if="!vatIncluded">
          €{{ buybackAmount?.toLocaleString(lang) }}
        </span>
        <span v-else>€{{ buybackAmountWithVat?.toLocaleString(lang) }}</span>
      </div>
      <div>
        <VueSlider
          v-model="buybackAmount"
          v-bind="sliderOptions"
          :min="buybackAmountConfiguration?.min"
          :max="buybackAmountConfiguration?.max"
          :interval="configuration.buybackAmount.interval"
          :tooltip-formatter="(val) => depositTooltipFormatted(val)"
          @change="onChange" />
      </div>
    </div>

    <div class="mb-24">
      <button
        type="button"
        class="btn btn-primary btn-full-width"
        @click="onClickRequestOffer">
        {{ dictionary.buttonRequestOfferText }}
      </button>
    </div>

    <div class="d-flex justify-content-center mb-24">
      <button
        type="button"
        class="btn btn-link btn-icon-start btn-edit"
        @click="onClickChangeVehicle">
        <i class="icon-edit"></i>
        <small>{{ dictionary.buttonChangeVehicleText }}</small>
      </button>
    </div>

    <hr class="separator mb-24" />

    <div class="b-leaseitnow-car-config__banners">
      <div><IdeaLottie width="20" /></div>
      <div v-html="sponsorsText"></div>
    </div>
  </div>
</template>

<script>
import * as lib from './../../../lib/lib'
import IdeaLottie from './../../components/lotties/Idea.vue'
import VueSlider from 'vue-slider-component'
import LazyImage from './../../components/common/LazyImage.vue'
import Alert from './../../components/common/Alert.vue'
import Loader from './../../components/common/Loader.vue'
import mixinsLeaseItNow from './../../mixins/leaseitnow-mixins'
import constants from './../../../lib/constants'

export default {
  components: { Alert, Loader, VueSlider, IdeaLottie, LazyImage },
  mixins: [mixinsLeaseItNow],
  props: {
    carObj: {
      required: true,
      type: Object,
    },
  },
  emits: ['change-config', 'change-vat', 'click-request'],
  data() {
    return {
      lang: 'en',
      isLoading: false,
      errorMsgs: [],
      sliderOptions: {
        lazy: true,
        duration: null,
        height: 10,
        contained: true,
      },
      vatIncluded: false,
      sponsorsText: null,
      price: JSON.parse(this.carObj.price),
    }
  },
  computed: {
    deposit: {
      get: function () {
        return this.$store.state.deposit
      },
      set: function (value) {
        this.setField({ field: 'deposit', value })
      },
    },
    numOfPayments: {
      get: function () {
        return this.$store.state.numOfPayments
      },
      set: function (value) {
        this.setField({ field: 'numOfPayments', value })
      },
    },
    annualKm: {
      get: function () {
        return this.$store.state.annualKm
      },
      set: function (value) {
        this.setField({ field: 'annualKm', value })
      },
    },
    buyback: {
      get: function () {
        return this.$store.state.buyback
      },
      set: function (value) {
        this.setField({ field: 'buyback', value })
      },
    },
    buybackAmount: {
      get: function () {
        return this.$store.state.buybackAmount
      },
      set: function (value) {
        this.setField({ field: 'buybackAmount', value })
      },
    },

    desopitWithVat() {
      const { vat } = this.configuration
      return lib.roundNumberBy100(this.deposit * (vat / 100 + 1))
    },

    buybackAmountWithVat() {
      const { vat } = this.configuration
      return lib.roundNumberBy100(this.buybackAmount * (vat / 100 + 1))
    },
    rentalPriceFormatted() {
      return this.rentalPrice
        ? Math.round(this.rentalPrice).toLocaleString(this.lang)
        : null
    },
    rentalPriceVatIncludedFormatted() {
      if (!this.rentalPrice) {
        return null
      }
      if (!this.vat) {
        return null
      }
      return Math.round(this.rentalPrice + this.vat).toLocaleString(this.lang)
    },

    depositConfiguration() {
      const { minPercentage, maxPercentage, factor } =
        this.configuration.deposit
      return {
        min: lib.roundNumberBy100((this.price / factor) * minPercentage),
        max: lib.roundNumberBy100((this.price / factor) * maxPercentage),
      }
    },

    buybackAmountConfiguration() {
      const { minPercentage, maxPercentage, factor } =
        this.configuration.buybackAmount
      return {
        min: lib.roundNumberBy100((this.price / factor) * minPercentage),
        max: lib.roundNumberBy100((this.price / factor) * maxPercentage),
      }
    },
    priceExclTaxFormatted() {
      return this.carObj
        ? Math.round(this.carObj.price_excl_tax_num).toLocaleString(this.lang)
        : null
    },

    priceFormatted() {
      return this.carObj
        ? this.carObj.price_num.toLocaleString(this.lang)
        : null
    },

    extraPriceFormatted() {
      return Math.round(this.extraPrice).toLocaleString(this.lang)
    },

    finalPriceFormatted() {
      return Math.round(this.finalPrice).toLocaleString(this.lang)
    },
  },
  mounted() {
    const { sponsorsText } = lib.getData('leaseItNowData')
    this.sponsorsText = sponsorsText

    this.lang = lib.getLocaleFromLangAttr()

    this.$nextTick(() => {
      lib.iniTooltips(document)
    })
  },
  methods: {
    depositTooltipFormatted(val) {
      const { vat } = this.configuration
      if (this.vatIncluded) {
        const vatVal = lib.roundNumberBy100(val * (vat / 100 + 1))
        return `€${vatVal.toLocaleString(this.lang)}`
      }
      return `€${val.toLocaleString(this.lang)}`
    },
    onClickChangeVehicle() {
      const { language } = this.$route.params
      const { sessionStorageKey } = constants.leaseitnow
      const searchargs = sessionStorage.getItem(sessionStorageKey)

      this.$router.push({
        name: 'home',
        params: { language },
        query: { searchargs: searchargs },
      })

      lib.scrollToTop()
    },

    onClickRequestOffer() {
      this.$emit('click-request')
    },

    onChange() {
      this.$nextTick(() => {
        this.$emit('change-config')
      })
    },

    onChangeBuyback() {
      this.$nextTick(() => {
        this.$emit('change-config')
        lib.iniTooltips(document)
      })
    },
    onChangeVat() {
      this.$emit('change-vat', this.vatIncluded)
    },
  },
}
</script>
